/**
 * Section Colors Service
 * 
 * Centralized service to provide consistent section and task colors
 * throughout the application.
 */

import { 
  getSectionBackgroundColor, 
  getSectionIconColor, 
  getSectionDescription,
  normalizeSectionId
} from '../utils/sectionColors';

/**
 * Get task colors based on section and task information
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.task - Task object with potential color information
 * @param {string|Object} params.section - Section ID or section object
 * @param {string} params.sectionId - Optional direct section ID
 * @param {string} params.sectionName - Optional section name for fallback
 * @param {string} params.status - Optional task status
 * @returns {Object} Object with backgroundColor and iconColor properties
 */
export const getTaskColors = ({ 
  task, 
  section, 
  sectionId, 
  sectionName,
  status
}) => {


  // 1. First priority: Use explicit task colors if available
  if (task?.backgroundColor && task?.iconColor) {
    return {
      backgroundColor: task.backgroundColor,
      iconColor: task.iconColor
    };
  }

  // 2. Second priority: Use section colors from task if available
  if (task?.sectionColors?.backgroundColor && task?.sectionColors?.iconColor) {
    return {
      backgroundColor: task.sectionColors.backgroundColor,
      iconColor: task.sectionColors.iconColor
    };
  }

  // 3. Check status-based colors
  const statusColors = getStatusColors(status || task?.status);
  if (statusColors) {
    return statusColors;
  }

  // 4. Get section ID from the available parameters
  const resolvedSectionId = 
    // Direct section ID parameter
    sectionId || 
    // Section ID from task
    task?.sectionId || 
    // Section ID from section object
    (section?._id || section?.id) ||
    // Normalize section name as fallback
    sectionName || 
    task?.sectionName;

  // 5. Return null if we've reached this point - don't use fallbacks
  return {
    backgroundColor: null,
    iconColor: null
  };
};

/**
 * Get colors based on task status
 * 
 * @param {string} status - Task status
 * @returns {Object|null} Colors object or null if status doesn't have specific colors
 */
export const getStatusColors = (status) => {
  if (!status) return null;
  switch (status.toLowerCase()) {
    case 'completed':
      return {
        backgroundColor: '#e8f5e9', // Light green
        iconColor: '#66bb6a' // Green
      };
    case 'in_progress':
      return {
        backgroundColor: '#e3f2fd', // Light blue
        iconColor: '#42a5f5' // Blue
      };
    case 'blocked':
      return {
        backgroundColor: '#ffebee', // Light red
        iconColor: '#f44336' // Red
      };
    case 'selected':
      return {
        backgroundColor: '#fef4cf', // Light yellow
        iconColor: '#f4c443' // Yellow
      };
    default:
      return null;
  }
};

/**
 * Get phase-based colors
 * 
 * @param {string} phase - Task phase
 * @returns {Object} Colors object
 */
export const getPhaseColors = (phase) => {
  if (!phase) {
    return {
      backgroundColor: '#f5f5f5', // Light gray for default
      iconColor: '#475969' // Slate for default
    };
  }

  const lowerPhase = phase.toLowerCase();
  
  if (lowerPhase.includes('setup') || lowerPhase.includes('infrastructure')) {
    return {
      backgroundColor: '#e3f2fd', // Light blue
      iconColor: '#42a5f5' // Blue
    };
  }
  
  if (lowerPhase.includes('core') || lowerPhase.includes('foundation')) {
    return {
      backgroundColor: '#e0f7fa', // Light cyan
      iconColor: '#26c6da' // Cyan
    };
  }
  
  if (lowerPhase.includes('feature') || lowerPhase.includes('implementation')) {
    return {
      backgroundColor: '#fff3e0', // Light orange
      iconColor: '#ff9800' // Orange
    };
  }
  
  if (lowerPhase.includes('test') || lowerPhase.includes('quality')) {
    return {
      backgroundColor: '#f3e5f5', // Light purple
      iconColor: '#ab47bc' // Purple
    };
  }
  
  if (lowerPhase.includes('deploy') || lowerPhase.includes('release')) {
    return {
      backgroundColor: '#e8f5e9', // Light green
      iconColor: '#66bb6a' // Green
    };
  }
  
  return {
    backgroundColor: '#f5f5f5', // Light gray for default
    iconColor: '#475969' // Slate for default
  };
};

export default {
  getTaskColors,
  getStatusColors,
  getPhaseColors,
  getSectionBackgroundColor,
  getSectionIconColor,
  getSectionDescription,
  normalizeSectionId
};